import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import {
  AddIcon,
  Button,
  DatePicker,
  Divider,
  Modal,
  Radio,
  Select,
  Spinner,
  Text,
  type TypeSelectOption,
  toastService,
} from '@thefarmersfront/kitchen';

import { FormField, KitchenTable, ModalContentsContainer, ModalSection } from '~/components/common';

import {
  useGetReturnDisposalInboundAreaQuery,
  useGetReturnDisposalIssueItemsQuery,
  useGetReturnDisposalOperationTimesQuery,
  useGetReturnDisposalRentalCompaniesQuery,
  usePutReturnDisposalRtsDirectMutation,
} from '~/hooks/receive';
import {
  changeRtsDirectRentalCard,
  formatDate,
  getReturnDisposalIssueItemsColumns,
  getReturnDisposalTimeOptions,
  isRtsDirectRentalCardValid,
  validateRtsDirectContactPhone,
} from '~/utils';

import {
  RETURN_DISPOSAL_REQUEST_MESSAGE,
  RETURN_DISPOSAL_RTS_METHOD,
  RTS_DIRECT_MAX_RENTAL_CARDS,
  RTS_DIRECT_PALLET_RELEASE,
  RTS_DIRECT_PALLET_RELEASE_OPTIONS,
  TOAST_MESSAGE,
  type TypeRtsDirectPalletRelease,
} from '~/constants';

import RtsDirectRentalCard from './RtsDirectRentalCard';

import type { TypeReturnDisposalIssueItemApi, TypeRtsDirectRentalCard } from '~/types/receive.types';

import styles from './ReceiveReturnDisposalRtsDirectModal.module.scss';

type TypeReceiveReturnDisposalRtsDirectModalProps = {
  isOpen: boolean;
  inspectionIds: number[];
  /** 선택 전건이 보류해제-회송 건인지 — 컬리 지정 회수예정일이 자동 노출되고 변경 불가 */
  isRtsPendingInput: boolean;
  onClose: () => void;
  /** 신청 성공 시(부모에서 목록 재조회 + 모달 닫기) */
  onSuccess: () => void;
};

/** 렌탈사 카드 초기값 생성 */
const createRentalCard = (id: string): TypeRtsDirectRentalCard => ({
  id,
  rentalCompany: null,
  businessCode: '',
  contactName: '',
  contactPhone: '',
  releaseAddress: '',
});

/**
 * [입고관리 > 회송/폐기 관리] 회수(직배) 처리 신청 모달
 *
 * @note 안내 패널 + 상품정보 미리보기 + 회수정보(회수지 주소·회수예정일·파렛트 반출·렌탈사 카드)를 입력해 신청한다.
 *       선택한 시간 슬롯의 `capaId`를, 파렛트 미반출 시 `rentals: []`를 전송한다.
 *       슬롯에 capaId 가 없으면 그 키를 생략한다(BE 가 지정 슬롯을 찾아 처리).
 *       보류해제-회송 건은 지정 슬롯(단건·capaId null)의 날짜·시간을 자동 표시해 잠근다.
 */
const ReceiveReturnDisposalRtsDirectModal = ({
  isOpen,
  inspectionIds,
  isRtsPendingInput,
  onClose,
  onSuccess,
}: TypeReceiveReturnDisposalRtsDirectModalProps) => {
  // 초기 카드 id가 'rental-1'이므로 1에서 시작(다음 추가분은 'rental-2'부터) — id 충돌 시 카드 정보가 서로 싱크됨
  const cardIdRef = useRef(1);
  const nextCardId = () => {
    cardIdRef.current += 1;
    return `rental-${cardIdRef.current}`;
  };

  const [recoveryDate, setRecoveryDate] = useState<Date | null>(null);
  const [recoveryTime, setRecoveryTime] = useState<TypeSelectOption<string> | null>(null);
  const [palletRelease, setPalletRelease] = useState<TypeRtsDirectPalletRelease>(RTS_DIRECT_PALLET_RELEASE.RELEASE);
  const [rentalCards, setRentalCards] = useState<TypeRtsDirectRentalCard[]>(() => [createRentalCard('rental-1')]);

  const issueItemsQuery = useGetReturnDisposalIssueItemsQuery({ inspectionIds, enabled: isOpen });
  const operationTimesQuery = useGetReturnDisposalOperationTimesQuery({
    inspectionIds,
    rtsMethod: RETURN_DISPOSAL_RTS_METHOD.DIRECT,
    enabled: isOpen,
  });
  const rentalCompaniesQuery = useGetReturnDisposalRentalCompaniesQuery({ enabled: isOpen });
  const inboundAreaQuery = useGetReturnDisposalInboundAreaQuery({ inspectionIds, enabled: isOpen });
  const submitMutation = usePutReturnDisposalRtsDirectMutation();

  const issueItems = issueItemsQuery.data ?? [];
  const operationTimes = operationTimesQuery.data ?? [];

  /** 회수지주소: inbound-area 조회 주소, 미조회 시 '-' (정보성 필드 → 실패해도 모달은 사용 가능) */
  const inboundAddress = inboundAreaQuery.data?.address || '-';

  // 보류해제-회송 건은 서버가 컬리 지정 회수예정일의 슬롯(단건·capaId null)만 내려준다.
  // 그 슬롯의 날짜·시간을 자동 표시하고 달력·시간 선택을 잠가(disabled) 변경을 막는다.
  useEffect(() => {
    if (!isRtsPendingInput || operationTimes.length === 0) return;

    const [earliestDate] = operationTimes.map((slot) => slot.date).sort();
    const [year, month, day] = earliestDate.split('-').map(Number);
    const [earliestSlotOption] = getReturnDisposalTimeOptions(operationTimes, earliestDate);

    setRecoveryDate(new Date(year, month - 1, day));
    setRecoveryTime(earliestSlotOption ?? null);
  }, [isRtsPendingInput, operationTimes]);

  // 상품정보·회수예정일·렌탈사 세 조회 모두 신청 폼 구성에 필요 → 로딩/에러를 합쳐 동일 상태 UI로 노출
  const isContentLoading = issueItemsQuery.isLoading || operationTimesQuery.isLoading || rentalCompaniesQuery.isLoading;
  const isContentError = issueItemsQuery.isError || operationTimesQuery.isError || rentalCompaniesQuery.isError;

  const columns = useMemo(() => getReturnDisposalIssueItemsColumns(), []);

  /** 회수 가능 날짜 집합 (DatePicker 활성 날짜 판별) */
  const availableDateSet = useMemo(() => new Set(operationTimes.map((slot) => slot.date)), [operationTimes]);

  /** 선택 날짜의 시간 슬롯 옵션 (동일 시작~종료 중복 제거, value: capaId) */
  const timeOptions = useMemo<TypeSelectOption<string>[]>(
    () => getReturnDisposalTimeOptions(operationTimes, formatDate(recoveryDate, 'YYYY-MM-DD')),
    [recoveryDate, operationTimes]
  );

  const rentalCompanyOptions = useMemo<TypeSelectOption<string>[]>(
    () => (rentalCompaniesQuery.data ?? []).map((code) => ({ value: code.code, text: code.name })),
    [rentalCompaniesQuery.data]
  );

  const isReleaseSelected = palletRelease === RTS_DIRECT_PALLET_RELEASE.RELEASE;
  const isRentalValid = !isReleaseSelected || (rentalCards.length > 0 && rentalCards.every(isRtsDirectRentalCardValid));
  const isValid = !!recoveryDate && !!recoveryTime && isRentalValid && !submitMutation.isPending;

  const handleChangeDate = (date: Date | null) => {
    setRecoveryDate(date);
    setRecoveryTime(null);
  };

  const handleChangeCard = (id: string, patch: Partial<TypeRtsDirectRentalCard>) => {
    setRentalCards((prev) => changeRtsDirectRentalCard(prev, id, patch));
  };

  // 입력이 끝난 시점에 그 카드의 연락처를 판정한다 (입력 중에는 에러가 계속 뜨지 않도록)
  const handleBlurCard = (id: string) => {
    setRentalCards((prev) => validateRtsDirectContactPhone(prev, id));
  };

  const handleAddCard = () => {
    setRentalCards((prev) =>
      prev.length < RTS_DIRECT_MAX_RENTAL_CARDS ? [...prev, createRentalCard(nextCardId())] : prev
    );
  };

  const handleRemoveCard = (id: string) => {
    setRentalCards((prev) => (prev.length > 1 ? prev.filter((card) => card.id !== id) : prev));
  };

  const handleSubmit = () => {
    if (!recoveryDate || !recoveryTime) return;

    const rentals = isReleaseSelected
      ? rentalCards.map((card) => ({
          rentalCompanyCode: card.rentalCompany?.value ?? '',
          businessCode: card.businessCode.trim(),
          contactName: card.contactName.trim(),
          contactPhone: card.contactPhone.trim(),
          releaseAddress: card.releaseAddress.trim(),
        }))
      : [];

    // 슬롯에 capaId 가 없으면(보류해제-회송 지정 슬롯) 미전송한다 — BE 가 지정 슬롯을 찾아 처리한다
    const capaId = Number(recoveryTime.value);

    submitMutation.mutate(
      { inspectionIds, ...(Number.isInteger(capaId) && { capaId }), rentals },
      {
        onSuccess: () => {
          toastService.addToast({ message: RETURN_DISPOSAL_REQUEST_MESSAGE.RTS_DIRECT_SUBMIT_SUCCESS });
          onSuccess();
        },
        onError: (error) => {
          toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
        },
      }
    );
  };

  return (
    <Modal
      title="회수(직배) 처리신청"
      size="xlarge"
      open={isOpen}
      confirmText="신청하기"
      disabled={!isValid}
      onCancel={onClose}
      onConfirm={handleSubmit}
    >
      {isContentLoading ? (
        <div className={styles['state-area']}>
          <Spinner />
        </div>
      ) : isContentError ? (
        <div className={styles['state-area']}>
          <Text size="large" color="tertiary">
            정보를 불러오지 못했습니다.
          </Text>
        </div>
      ) : (
        <ModalContentsContainer className={styles['modal-body']}>
          <ModalSection title="상품 정보">
            <KitchenTable<TypeReturnDisposalIssueItemApi>
              className={styles['preview-table']}
              data={issueItems}
              columns={columns}
              rowHeight={40}
              maxHeight="220px"
              loading={issueItemsQuery.isLoading}
              error={issueItemsQuery.isError}
            />
          </ModalSection>

          <ModalSection title="회수 정보">
            <FormField label="회수지주소" contentAlign="center" contents={<Text size="large">{inboundAddress}</Text>} />
            <FormField
              label="회수예정일"
              required
              tooltip={{
                description: isRtsPendingInput
                  ? RETURN_DISPOSAL_REQUEST_MESSAGE.RECOVERY_DATE_FIXED_TOOLTIP
                  : RETURN_DISPOSAL_REQUEST_MESSAGE.RECOVERY_DATE_TOOLTIP,
              }}
              contents={
                <div className={styles['date-time-inputs']}>
                  <DatePicker
                    size="small"
                    placeholder="날짜 선택"
                    maxWidth="229px"
                    portalTarget={document.body}
                    shouldCloseOnNotInView
                    disabled={isRtsPendingInput}
                    value={recoveryDate}
                    isDateDisabled={(date) => {
                      const formattedDate = formatDate(date, 'YYYY-MM-DD');
                      return formattedDate ? !availableDateSet.has(formattedDate) : false;
                    }}
                    onChange={handleChangeDate}
                  />
                  <Select
                    size="small"
                    placeholder="시간 선택"
                    maxWidth="317px"
                    dropdownPortalTarget={document.body}
                    shouldCloseOnNotInView
                    disabled={isRtsPendingInput || !recoveryDate}
                    value={recoveryTime}
                    options={timeOptions}
                    onChange={(_: unknown, option: TypeSelectOption<string> | null) => setRecoveryTime(option ?? null)}
                  />
                </div>
              }
            />
            <FormField
              label="렌탈 파렛트"
              required
              contentAlign="center"
              contents={
                <div className={styles['radio-group']}>
                  {RTS_DIRECT_PALLET_RELEASE_OPTIONS.map((option) => (
                    <Radio
                      key={option.value}
                      size="medium"
                      value={option.value}
                      label={option.label}
                      checked={palletRelease === option.value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setPalletRelease(e.target.value as TypeRtsDirectPalletRelease)
                      }
                    />
                  ))}
                </div>
              }
            />
            {isReleaseSelected && (
              <>
                {rentalCards.map((card) => (
                  <Fragment key={card.id}>
                    <Divider className={styles['section-divider']} orientation="horizontal" color="gray200" />
                    <RtsDirectRentalCard
                      card={card}
                      rentalCompanyOptions={rentalCompanyOptions}
                      showRemove={rentalCards.length > 1}
                      onChange={handleChangeCard}
                      onBlur={handleBlurCard}
                      onRemove={handleRemoveCard}
                    />
                  </Fragment>
                ))}
                {rentalCards.length < RTS_DIRECT_MAX_RENTAL_CARDS && (
                  <div className={styles['rental-action']}>
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      leftIcon={<AddIcon />}
                      onClick={handleAddCard}
                    >
                      렌탈사 추가
                    </Button>
                  </div>
                )}
              </>
            )}
          </ModalSection>
        </ModalContentsContainer>
      )}
    </Modal>
  );
};

export default ReceiveReturnDisposalRtsDirectModal;
